import objectPath from 'object-path'
import { createDesignerData } from 'designer/component/action/Design'
import { getCurrentUi } from 'designer/component/action/Data'
import { getFirstDefinedValue } from 'designer/component/component/modalReceiveData.util'

const clearCondition = ({ action, formData, designContext }) => {
  let { setFormData } = designContext
  let { formId } = action
  if (formId) {
    objectPath.set(formData, `@condition.${formId}`, {})
  }
  setFormData(formData)
  return formData
}

const getModal = (form, modalKey, designContext) => {
  let { findUischemaIndex } = designContext
  let elements = form.uischema.elements
  let mapKey = form.schema.mapKey[modalKey]
  let indices = findUischemaIndex(modalKey, mapKey, elements)
  let modalElement = indices.reduce((o, i) => o.elements[i], { elements })
  return modalElement
}

const closeInternalModal = ({ modalKey, designContext, setOpenModal }) => {
  let { jsonForm, setUischema } = designContext
  let elements = jsonForm.uischema.elements
  let modalElement = getModal(jsonForm, modalKey, designContext)
  modalElement.isOpen = false
  let newUiSchema = jsonForm.uischema
  newUiSchema.elements = elements
  setUischema(newUiSchema)
  setOpenModal(null)
}

const createComponentPropsData = ({
  sendProps,
  formData,
  action,
  designContext,
}) => {
  const { jsonForm, setComponentPropsData, isUiChanged } = designContext
  let datasourceValue = objectPath.get(designContext, `@datasource`) || {}
  let designerPropsData = objectPath.get(jsonForm, 'designerPropsData') || {}
  let componentPropsData = {
    data: formData,
    isClosed: true,
    send: sendProps,
    receive: designerPropsData.receive,
    isRefreshTable: action?.isRefreshTable,
  }
  let formGroup = jsonForm.formGroup || []
  sendProps.forEach(({ key, prop, propType, dynamicName, jsonKey }) => {
    if (propType === 'input') {
      let fullPropKey = jsonKey ? `${prop}.${jsonKey}` : prop
      let value = getFirstDefinedValue(formData, [
        key,
        fullPropKey,
        prop,
      ])
      objectPath.set(componentPropsData, `data.${prop}`, value)
    } else if (propType === 'condition') {
      objectPath.set(componentPropsData, `data.${prop}`, 1)
    } else if (propType === 'formData') {
      let { json } = formGroup.find((form) => form.key === key) || {}
      if (json) {
        Object.keys(json).forEach((itemKey) => {
          let value = objectPath.get(formData, itemKey)
          objectPath.set(componentPropsData, `data.${prop}.${itemKey}`, value)
        })
      }
    } else if (propType === 'datasource') {
      let value = objectPath.get(datasourceValue, dynamicName)
      objectPath.set(componentPropsData, `data.${prop}`, value)
    } else if (propType === 'tableSelection') {
      let value = objectPath.get(isUiChanged, ['tableSelection', key])
      objectPath.set(componentPropsData, `data.${prop}`, value)
    }
  })
  objectPath.set(componentPropsData, 'rowData', null)
  setComponentPropsData(componentPropsData)
  return componentPropsData
}

const getActionProps = (action = {}) => {
  let props = objectPath.get(action, 'props')
  if (!props) return []
  if (Array.isArray(props)) return props
  if (typeof props === 'object') return Object.values(props)
  return []
}

export const createModal = ({
  uiKey,
  formCode,
  formData,
  modalAction,
  designContext,
}) => {
  let {
    jsonForm,
    isUiChanged,
    setIsUiChanged,
    setDesignerPropsData,
    searchData,
  } = designContext
  let designerPropsData = objectPath.get(jsonForm, 'designerPropsData') || {}
  objectPath.set(designerPropsData, 'modal', modalAction.formCode)
  objectPath.set(designerPropsData, 'sourceType', modalAction.sourceType)
  designerPropsData = createDesignerData(
    designerPropsData,
    searchData,
    formData,
    getActionProps(modalAction),
  )
  setDesignerPropsData(designerPropsData)
  let ui
  if (uiKey) {
    ui = getCurrentUi(uiKey, jsonForm.schema, jsonForm.uischema)
  }
  let modal = {
    key: true,
    modalAction,
    isOpenModal: true,
    isOpening: false,
    designerPropsData,
    isHasUi: formCode ? true : false,
    ui: ui && ui.key === uiKey ? ui : { key: formCode },
  }
  objectPath.set(isUiChanged, ['modal', formCode], modal)
  if (modal.isHasUi) {
    objectPath.set(isUiChanged, ['triggerModal', uiKey], formCode)
  }
  setIsUiChanged(isUiChanged)
}

export const clearModal = ({ designContext }) => {
  let { jsonForm, isUiChanged, setIsUiChanged, setDesignerPropsData, setOpenModal } =
    designContext
  let designerPropsData = objectPath.get(jsonForm, 'designerPropsData') || {}
  let modalCode = designerPropsData.modal
  setOpenModal(null)
  setIsUiChanged((current) => {
    let modalKey = objectPath.get(current, ['modal', modalCode, 'ui', 'key']) ||
      objectPath.get(isUiChanged, ['modal', modalCode, 'ui', 'key'])
    let triggerModal = { ...current.triggerModal }
    let modal = { ...current.modal }
    if (modalKey) delete triggerModal[modalKey]
    delete modal[modalCode]
    return { ...current, triggerModal, modal }
  })
  objectPath.set(designerPropsData, 'modal', null)
  setDesignerPropsData(designerPropsData)
}

export const openModal = async ({
  action,
  data,
  designContext,
  uischema,
  setModalAction,
  setOpenModal,
}) => {
  let { formData } = data
  let buttonKey = uischema.key
  formData = clearCondition({ action, formData, designContext })
  if (setModalAction) {
    setModalAction(action)
  }
  let formCode = ''
  if (action.modalType === 'external') {
    formCode = action.formCode
  } else if (action.modalType === 'component') {
    formCode = action.component
  } else if (action.modalType === 'assessment') {
    formCode = action.assessmentCode
  }
  createModal({
    uiKey: buttonKey,
    formCode,
    formData,
    modalAction: action,
    designContext,
  })
  setOpenModal(buttonKey)
}

export const onTriggerModal = ({ modalKey, designContext, isOpening }) => {
  let { isUiChanged, setIsUiChanged } = designContext
  const triggerModal = objectPath.get(isUiChanged, 'triggerModal') || {}
  const modalCode = triggerModal[modalKey]
  const modalData = modalCode
    ? objectPath.get(isUiChanged, ['modal', modalCode])
    : null
  if (isOpening) {
    if (modalCode) {
      let nextModalData = modalData || {}
      objectPath.set(nextModalData, 'isOpening', isOpening)
      objectPath.set(isUiChanged, ['modal', modalCode], nextModalData)
    }
  } else {
    objectPath.del(isUiChanged, ['triggerModal', modalKey])
  }
  setIsUiChanged(isUiChanged)
}

export const closeModal = async ({ action, data, designContext }) => {
  let { isUiChanged, setIsUiChanged } = designContext
  let { formData } = data
  if (action.modalType === 'external' || action.modalType === 'component') {
    let sendProps = objectPath.get(action, 'props') || []
    sendProps = typeof sendProps ? Object.values(sendProps) : sendProps
    createComponentPropsData({
      sendProps,
      formData,
      action,
      designContext,
    })
    clearModal({ designContext })
  }
  return { status: 200, data: formData, message: 'success' }
}
